import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import type { AppUser } from "../types/user";

interface Props {
  user: AppUser;
  onGoToSettings: () => void;
}

function daysRemaining(deadline: string): number {
  const ms = new Date(deadline).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

const DISMISS_KEY = "2fa_notice_dismissed_at";

function wasDismissedToday(): boolean {
  const val = sessionStorage.getItem(DISMISS_KEY);
  if (!val) return false;
  // Dismiss lasts for the current browser session (sessionStorage clears on tab close)
  return true;
}

export default function TwoFAGraceNotice({ user, onGoToSettings }: Props) {
  const [dismissed, setDismissed] = useState(wasDismissedToday);
  const location = useLocation();

  if (user.totp_enabled || !user.totp_grace_deadline) return null;

  // On the settings page, never block — the user needs to reach the 2FA setup form.
  if (location.pathname === "/settings") return null;

  const days = daysRemaining(user.totp_grace_deadline);
  const isOverdue = days <= 0;

  function handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  // Overdue: full-screen blocking overlay — cannot be dismissed
  if (isOverdue) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <ShieldAlert size={28} className="text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Two-factor authentication required</h2>
          <p className="mt-3 text-sm text-gray-500">
            Your grace period has ended. You must set up 2FA before you can continue using Betable AI.
          </p>
          <button
            onClick={onGoToSettings}
            className="mt-6 w-full rounded-xl bg-[#1D345F] px-6 py-3 text-sm font-medium text-white hover:bg-[#162847]"
          >
            Set up 2FA now
          </button>
        </div>
      </div>
    );
  }

  // Within grace period: dismissible banner at top
  if (dismissed) return null;

  const urgency = days <= 2 ? "red" : days <= 4 ? "amber" : "blue";
  const colours = {
    red:   { bg: "bg-red-50 border-red-200",   icon: "text-red-600",   text: "text-red-800",   btn: "bg-red-600 hover:bg-red-700",   dismiss: "text-red-400 hover:text-red-600" },
    amber: { bg: "bg-amber-50 border-amber-200", icon: "text-amber-600", text: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700", dismiss: "text-amber-400 hover:text-amber-600" },
    blue:  { bg: "bg-blue-50 border-blue-200",  icon: "text-blue-600",  text: "text-blue-800",  btn: "bg-[#1D345F] hover:bg-[#162847]", dismiss: "text-blue-400 hover:text-blue-600" },
  }[urgency];

  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2.5 ${colours.bg}`}>
      <ShieldAlert size={16} className={`shrink-0 ${colours.icon}`} />
      <p className={`flex-1 text-sm ${colours.text}`}>
        <strong>Set up two-factor authentication</strong> — you have{" "}
        <strong>{days} {days === 1 ? "day" : "days"}</strong> remaining before access is restricted.
      </p>
      <button
        onClick={onGoToSettings}
        className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-white ${colours.btn}`}
      >
        Set up 2FA
      </button>
      <button
        onClick={handleDismiss}
        className={`shrink-0 rounded p-1 ${colours.dismiss}`}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
